import { buildSearchParams, urlBuilder } from '@starjack/utils';

import { BackendType, FrontendType, ObjectType } from '@starjack/utils';
import { createEntityApi, type EntityEndpointBuilder } from '@starjack/api';
import { convertTicketToFrontend, Ticket } from '../model/ticket.types';
import { formatStarjackDateToBackendString } from '@starjack/utils';

export type GetTicketsSearchParams<T extends ObjectType = FrontendType> = {
    userId?: string;
    orderId?: string;
    query?: string;
    orderDateFrom?: T extends FrontendType ? Date : string;
    orderDateTo?: T extends FrontendType ? Date : string;
    ticketStartFrom?: T extends FrontendType ? Date : string;
    ticketStartTo?: T extends FrontendType ? Date : string;
    sort?: string;
    size?: number;
    page?: number;
    hasInsurance?: boolean;
};

export const convertGetTicketsSearchParamsToBackend = (
    params: GetTicketsSearchParams
): GetTicketsSearchParams<BackendType> => ({
    ...params,
    orderDateFrom: formatStarjackDateToBackendString(params.orderDateFrom),
    orderDateTo: formatStarjackDateToBackendString(params.orderDateTo),
    ticketStartFrom: formatStarjackDateToBackendString(params.ticketStartFrom),
    ticketStartTo: formatStarjackDateToBackendString(params.ticketStartTo),
});

export type GetTicketsResponse<T extends ObjectType = FrontendType> = {
    content: Ticket<T>[];
    size: number;
    totalElements: number;
    totalPages: number;
};

const ticketUrls = (id = '') => ({
    tickets: urlBuilder('order', 'tickets'),
    ticketsPaged: urlBuilder('order', 'liftoperators/tickets-paged'),
    ticketCancel: urlBuilder('order', `tickets/${id}/cancel`),
    ticketPermissionStatus: urlBuilder('bei', `tickets/${id}`),
});

/**
 * Type definition for the API with injected Ticket hooks
 */
export interface TicketApiHooks {
    useGetTicketsQuery: (params: GetTicketsSearchParams) => {
        data?: GetTicketsResponse;
        isFetching: boolean;
        isLoading: boolean;
        error?: unknown;
    };
    usePostTicketCancellationMutation: () => [
        (params: { ticketId: string; refund: boolean; reason?: string }) => {
            unwrap: () => Promise<void>;
        },
        { isLoading: boolean; error?: unknown },
    ];
    usePostTicketCancelInsuranceMutation: () => [
        (params: { ticketId: string }) => { unwrap: () => Promise<Response> },
        { isLoading: boolean; error?: unknown },
    ];
    usePostTicketRevokeInsuranceMutation: () => [
        (params: { ticketId: string }) => { unwrap: () => Promise<Response> },
        { isLoading: boolean; error?: unknown },
    ];
    usePostTicketCancelInsuranceFromInsuranceMutation: () => [
        (params: { ticketId: string }) => { unwrap: () => Promise<Response> },
        { isLoading: boolean; error?: unknown },
    ];
    useGetTicketPermissionStatusQuery: (
        params: { ticketId: string },
        options?: { skip?: boolean }
    ) => {
        data?: { id: string; permissionStatuses: string[] };
        isLoading: boolean;
        error?: unknown;
        isError: boolean;
    };
}

/**
 * Ticket endpoint definitions
 */
export const ticketEndpoints = (builder: EntityEndpointBuilder) => ({
    getTickets: builder.query<GetTicketsResponse, GetTicketsSearchParams>({
        query: (params) => {
            const paramsCopy = { ...params };

            if (!paramsCopy.hasInsurance) {
                delete paramsCopy.hasInsurance;
            }

            return `${ticketUrls().ticketsPaged}${buildSearchParams(
                convertGetTicketsSearchParamsToBackend(paramsCopy)
            )}`;
        },

        transformResponse: (response: GetTicketsResponse<BackendType>) => {
            return {
                ...response,
                content: response.content.map(convertTicketToFrontend),
            };
        },
        providesTags: ['TICKETS'],
    }),

    postTicketCancellation: builder.mutation<
        void,
        { ticketId: string; refund: boolean; reason?: string }
    >({
        query: ({ ticketId, refund, reason }) => ({
            url: `${ticketUrls(ticketId).ticketCancel}`,
            method: 'POST',
            body: {
                ticketId,
                refund,
                reason,
            },
        }),
        invalidatesTags: ['TICKETS'],
    }),

    postTicketCancelInsurance: builder.mutation<Response, { ticketId: string }>(
        {
            query: ({ ticketId }) => ({
                url: `${ticketUrls().tickets}/${ticketId}/cancel-insurance`,
                method: 'POST',
                body: { ticketId },
            }),
            invalidatesTags: ['TICKETS'],
        }
    ),

    postTicketRevokeInsurance: builder.mutation<Response, { ticketId: string }>(
        {
            query: ({ ticketId }) => ({
                url: `${ticketUrls().tickets}/${ticketId}/revoke-insurance`,
                method: 'POST',
                body: { ticketId },
            }),
            invalidatesTags: ['TICKETS'],
        }
    ),

    postTicketCancelInsuranceFromInsurance: builder.mutation<
        Response,
        { ticketId: string }
    >({
        query: ({ ticketId }) => ({
            url: `${ticketUrls().tickets}/${ticketId}/embargo-insurance`,
            method: 'POST',
            body: { ticketId },
        }),
        invalidatesTags: ['TICKETS'],
    }),

    getTicketPermissionStatus: builder.query<
        { id: string; permissionStatuses: string[] },
        { ticketId: string }
    >({
        query: ({ ticketId }) => ticketUrls(ticketId).ticketPermissionStatus,
        providesTags: ['TICKETS'],
    }),
});

export const ticketApi = createEntityApi({
    tagTypes: ['TICKETS'],
    endpoints: ticketEndpoints,
});

// Export hooks with explicit types for TypeScript inference
export const useGetTicketsQuery: TicketApiHooks['useGetTicketsQuery'] =
    ticketApi.useGetTicketsQuery;
export const usePostTicketCancellationMutation: TicketApiHooks['usePostTicketCancellationMutation'] =
    ticketApi.usePostTicketCancellationMutation;
export const usePostTicketCancelInsuranceMutation: TicketApiHooks['usePostTicketCancelInsuranceMutation'] =
    ticketApi.usePostTicketCancelInsuranceMutation;
export const usePostTicketRevokeInsuranceMutation: TicketApiHooks['usePostTicketRevokeInsuranceMutation'] =
    ticketApi.usePostTicketRevokeInsuranceMutation;
export const usePostTicketCancelInsuranceFromInsuranceMutation: TicketApiHooks['usePostTicketCancelInsuranceFromInsuranceMutation'] =
    ticketApi.usePostTicketCancelInsuranceFromInsuranceMutation;
export const useGetTicketPermissionStatusQuery: TicketApiHooks['useGetTicketPermissionStatusQuery'] =
    ticketApi.useGetTicketPermissionStatusQuery;
